import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDateTime } from '@/utils/format';
import { RequestStatusBadge } from './StatusBadge';
import RequestDetailModal from './RequestDetailModal';
import ActivationModal from './ActivationModal';
import type { AdminRequest } from '@/mocks/adminRequests';
import type { ActivateResult } from '@/hooks/useAdminData';

type RequestsTabProps = {
  requests: AdminRequest[];
  managerEmail: string;
  onActivate: (requestId: string, code: string, manager: string) => Promise<ActivateResult>;
};

export default function RequestsTab({ requests, managerEmail, onActivate }: RequestsTabProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [detailRequest, setDetailRequest] = useState<AdminRequest | null>(null);
  const [activateRequest, setActivateRequest] = useState<AdminRequest | null>(null);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return requests.filter((request) => {
      const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
      const matchesSearch =
        !query ||
        request.reference.toLowerCase().includes(query) ||
        request.clientName.toLowerCase().includes(query) ||
        request.fullName.toLowerCase().includes(query) ||
        request.bankName.toLowerCase().includes(query);
      return matchesStatus && matchesSearch;
    });
  }, [requests, search, statusFilter]);

  const canActivate = (request: AdminRequest) =>
    request.status === 'awaiting_activation' ||
    request.status === 'code_sent' ||
    request.status === 'new' ||
    request.status === 'registered';

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-xl font-semibold text-foreground-950">
          {t('admin.requests.title')}
        </h1>
        <p className="mt-1 text-sm text-foreground-600">{t('admin.requests.subtitle')}</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('admin.requests.searchPh')}
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
        >
          <option value="all">{t('admin.common.all')}</option>
          {(
            [
              'new',
              'registered',
              'code_sent',
              'awaiting_activation',
              'activation_validated',
              'processing',
              'processed',
              'cancelled',
              'expired',
            ] as const
          ).map((status) => (
            <option key={status} value={status}>
              {t(`admin.status.${status}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100 text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-5 py-3 font-semibold">{t('admin.requests.reference')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.requests.beneficiary')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.requests.amount')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.requests.bank')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.requests.status')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.requests.date')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.requests.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-foreground-500">
                    {t('admin.requests.empty')}
                  </td>
                </tr>
              ) : (
                filtered.map((request) => (
                  <tr key={request.id} className="border-b border-background-100 last:border-0 hover:bg-background-50">
                    <td className="px-5 py-3">
                      <p className="font-semibold text-foreground-800">{request.reference}</p>
                      <p className="text-xs text-foreground-500">{request.clientName}</p>
                    </td>
                    <td className="px-5 py-3 text-foreground-700">{request.fullName}</td>
                    <td className="px-5 py-3 font-semibold text-foreground-900">
                      {formatCurrency(request.amount, request.currency)}
                    </td>
                    <td className="px-5 py-3 text-foreground-700">{request.bankName}</td>
                    <td className="px-5 py-3">
                      <RequestStatusBadge status={request.status} />
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-foreground-600">
                      {formatDateTime(request.createdAt)}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setDetailRequest(request)}
                          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                        >
                          <i className="ri-eye-line"></i>
                          {t('admin.requests.details')}
                        </button>
                        {canActivate(request) && (
                          <button
                            type="button"
                            onClick={() => setActivateRequest(request)}
                            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-2.5 py-1.5 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                          >
                            <i className="ri-play-circle-line"></i>
                            {t('admin.requests.activate')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detailRequest && (
        <RequestDetailModal request={detailRequest} onClose={() => setDetailRequest(null)} />
      )}

      {activateRequest && (
        <ActivationModal
          request={activateRequest}
          managerEmail={managerEmail}
          onClose={() => setActivateRequest(null)}
          onActivate={onActivate}
        />
      )}
    </div>
  );
}